import { useCallback, useEffect, useState } from 'react';

import { runAssessmentPreflight, type CardPreflightResultDTO } from '@/api/assessments/api';

export interface CardPreflightResult {
  id: string;
  valid: boolean;
  reason: string | null;
  recoveryAction: string | null;
}

export interface UseStep2PreflightOptions {
  assessmentId: string;
  accessToken: string | null;
  clientId: string;
  projectId: string;
  transcriptCardIds: string[];
  clientDocCardIds: string[];
}

export interface UseStep2PreflightResult {
  isValidating: boolean;
  transcriptGatePassed: boolean;
  transcriptResults: CardPreflightResult[];
  clientDocResults: CardPreflightResult[];
  step1Approved: boolean;
  runPreflight: () => Promise<void>;
}

function toResult(dto: CardPreflightResultDTO): CardPreflightResult {
  return {
    id: dto.card_id,
    valid: dto.valid,
    reason: dto.reason,
    recoveryAction: dto.recovery_action,
  };
}

export function useStep2Preflight({
  assessmentId,
  accessToken,
  clientId,
  projectId,
  transcriptCardIds,
  clientDocCardIds,
}: UseStep2PreflightOptions): UseStep2PreflightResult {
  const [isValidating, setIsValidating] = useState(false);
  const [transcriptGatePassed, setTranscriptGatePassed] = useState(false);
  const [transcriptResults, setTranscriptResults] = useState<CardPreflightResult[]>([]);
  const [clientDocResults, setClientDocResults] = useState<CardPreflightResult[]>([]);
  const [step1Approved, setStep1Approved] = useState(false);

  const executePreflight = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken) {
        return;
      }
      setIsValidating(true);
      try {
        const response = await runAssessmentPreflight(
          assessmentId,
          accessToken,
          clientId,
          projectId,
        );
        if (signal?.aborted) {
          return;
        }
        setStep1Approved(response.step1_approved);
        setTranscriptGatePassed(response.transcript_gate_passed);
        setTranscriptResults(response.transcript_results.map(toResult));
        setClientDocResults(response.client_doc_results.map(toResult));
      } catch {
        if (signal?.aborted) {
          return;
        }
        // On network error keep prior gate state; button stays disabled
      } finally {
        if (!signal?.aborted) {
          setIsValidating(false);
        }
      }
    },
    [assessmentId, accessToken, clientId, projectId],
  );

  // Stable string keys for effect deps — avoids re-firing when parent passes new array literals
  // with identical content on each render. Reference equality of the arrays is not the trigger;
  // content change is.
  const transcriptKey = transcriptCardIds.join('\x00');
  const clientDocKey = clientDocCardIds.join('\x00');

  // Re-run when card list content changes; abort the previous in-flight request
  useEffect(() => {
    const controller = new AbortController();
    void executePreflight(controller.signal);
    return () => {
      controller.abort();
    };
  }, [transcriptKey, clientDocKey, executePreflight]);

  const runPreflight = useCallback(async (): Promise<void> => {
    await executePreflight();
  }, [executePreflight]);

  return {
    isValidating,
    transcriptGatePassed,
    transcriptResults,
    clientDocResults,
    step1Approved,
    runPreflight,
  };
}
